import React from "react";

import getMeasureSeries from "../Chart/series/getMeasureSeries";
import type { SelectedQuantity } from "../useSelectedQuantities";
import getQuantityFlagSerie from "../Chart/series/getQuantityFlagSerie";
import getAnalysisFlagSerie from "../Chart/series/getAnalysisFlagSerie";
import getAllDataFlagSerie from "../Chart/series/getAllDataFlagSerie";
import type { Project } from "../../../api/useProjects";
import { useSetups } from "../../../api/useSetups";
import type { Analysis } from "../../../api/Analysis";
import type { MeasuresChartType } from "./MeasuresChartType";
import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import useActivationFilters from "../../../api/useActivationFilters";
import { indexBy } from "diagnostics-lib/iterable";
import { useMetrologyOverrides } from "../../../api/useMetrologyOverrides";
import { useDisplayInvalidPoints } from "../Analysis/ToggleDisplayInvalidPoints";
import { useQuantityIndex } from "../../../api/useQuantities";

function useSeries(
  project: Project,
  analyses: Analysis[],
  activeAnalyses: ActiveAnalysis[],
  selectedQuantities: SelectedQuantity[],
  axisGroupedByUnit: boolean,
  actionAllowed: boolean,
  selectedChartOption: MeasuresChartType,
  isAllDataMode: boolean,
  publicationFlagDisplayed: boolean,
): (Highcharts.SeriesScatterOptions | Highcharts.SeriesLineOptions)[] {
  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: project.id,
  });
  const setups = useSetups(project.id);
  const activationFilters = useActivationFilters(project.id);
  const activeAnalysisIndex = React.useMemo(() => indexBy(activeAnalyses, "id"), [activeAnalyses]);
  const metrologyOverrides = useMetrologyOverrides(project.id);
  const quantityIndex = useQuantityIndex(project.id);

  return React.useMemo(() => {
    const measureSeries = getMeasureSeries({
      analyses: analyses.map(a => activeAnalysisIndex.get(a.id) ?? a),
      activationFilters,
      selectedQuantities,
      axisGroupedByUnit,
      actionAllowed,
      symptomMode: false,
      selectedChartOption,
      metrologyOverrides,
      displayInvalidPoints,
      quantityIndex,
    });

    const noValueSerie = getQuantityFlagSerie({
      analyses,
      quantities: selectedQuantities.map(q => q.quantity),
      withSymptomFlags: false,
      chartType: selectedChartOption,
      setups,
      metrologyOverrides,
      displayInvalidPoints,
    });

    const analysisFlagSerie = getAnalysisFlagSerie({
      project,
      setups,
      analyses,
      quantityIndex,
      chartType: isAllDataMode ? "MEASURES" : "SYMPTOMS",
      publicationFlagDisplayed,
    });

    const series = [noValueSerie, ...measureSeries, analysisFlagSerie];

    if (isAllDataMode) {
      const allDataFlagSerie = getAllDataFlagSerie({
        analyses,
        activeAnalyses,
      });
      series.push(allDataFlagSerie);
    }

    return series;
  }, [
    project,
    analyses,
    activationFilters,
    selectedQuantities,
    axisGroupedByUnit,
    actionAllowed,
    selectedChartOption,
    metrologyOverrides,
    displayInvalidPoints,
    quantityIndex,
    setups,
    isAllDataMode,
    publicationFlagDisplayed,
    activeAnalysisIndex,
    activeAnalyses,
  ]);
}

export default useSeries;
